import React, { useState } from 'react'
import { Share2, MoreHorizontal, Edit3 } from 'lucide-react'
import { Match } from '../../types'
import { getApiUrl } from '../../config'
import { useResponsive } from '../../hooks/useResponsive'
import { User } from '../../contexts/AuthContext'
import MatchJoinHistory from '../MatchJoinHistory'

interface ImprovedMatchActionsProps {
  match: Match
  userIsCreator: boolean
  onShowNotification: (message: string, type: "success" | "error") => void
  onFinishMatch?: () => void
  hasReport?: boolean
  onTransferAdmin?: () => void
  currentUser?: User | null
  reportCompleted?: boolean
  onFinalizeReport?: () => void
  onCreateReview?: (player: any) => void
  onUploadFinalScore?: () => void
  onLoadReport?: () => void
  onVote?: () => void
  onEditLocation?: () => void
  hasGuestPlayers?: boolean
  canVote?: boolean
  isMobile?: boolean
  onRecalculateMatchResult?: () => void
  onImportList?: () => void
  onSyncTeams?: () => Promise<void>
  onToggleTactics?: () => void
  showTacticalField?: boolean
  onSetTacticalField?: (value: boolean) => void
  canViewTactics?: boolean
  onReportStats?: () => void
  setShowShareModal?: (show: boolean) => void
  pendingGuestRequestsCount?: number
  onViewGuestRequests?: () => void
  onManageGuests?: () => void
  onViewJoinApplications?: () => void
  isUserInMatch?: boolean
  onSuggestTeams?: () => void
  onRepeatMatch?: () => void
  onCancelMatch?: () => void
}

export const ImprovedMatchActions: React.FC<ImprovedMatchActionsProps> = ({
  match,
  userIsCreator,
  onShowNotification,
  onFinishMatch,
  hasReport,
  onTransferAdmin,
  currentUser,
  reportCompleted,
  onFinalizeReport,
  onCreateReview,
  onUploadFinalScore,
  onLoadReport,
  onVote,
  onEditLocation,
  hasGuestPlayers,
  canVote,
  isMobile,
  onRecalculateMatchResult,
  onImportList,
  onSyncTeams,
  onToggleTactics,
  showTacticalField,
  onSetTacticalField,
  canViewTactics,
  onReportStats,
  setShowShareModal,
  pendingGuestRequestsCount = 0,
  onViewGuestRequests,
  onManageGuests,
  onViewJoinApplications,
  isUserInMatch = false,
  onSuggestTeams,
  onRepeatMatch,
  onCancelMatch,
}) => {
  const { isMobile: responsiveIsMobile } = useResponsive()
  const [showAdminMenu, setShowAdminMenu] = useState(false)
  const [_showUtilityMenu, setShowUtilityMenu] = useState(false)

  const mobile = isMobile !== undefined ? isMobile : responsiveIsMobile

  const handleAddToCalendar = () => {
    try {
      if (!currentUser || !currentUser.id) {
        onShowNotification("Error: Usuario no autenticado", "error")
        return
      }

      const isPlayerInMatch =
        match.players.some((player) => player.userId === currentUser.id) ||
        match.substitutes.some((player) => player.userId === currentUser.id)

      if (!isPlayerInMatch && !userIsCreator) {
        onShowNotification("Solo los jugadores del partido pueden agregar el evento al calendario", "error")
        return
      }

      const matchDateTime = new Date(`${match.date}T${match.time}`)
      const formatDateForGoogle = (date: Date) => {
        return date
          .toISOString()
          .replace(/[-:]/g, "")
          .replace(/\.\d{3}/, "")
      }

      const startDate = formatDateForGoogle(matchDateTime)
      const endDate = formatDateForGoogle(new Date(matchDateTime.getTime() + 1 * 60 * 60 * 1000))

      const description = `⚽ 

⏰ Horario: ${match.time}
📍 Cancha: ${match.location}
💰 Costo por persona: $${match.price}
${match.mapsUrl ? `🗺️ Ver en Google Maps: ${match.mapsUrl}` : ""}

¡No te olvides de llevar los botines!`

      const googleCalendarUrl = new URL("https://calendar.google.com/calendar/render")
      googleCalendarUrl.searchParams.set("action", "TEMPLATE")
      googleCalendarUrl.searchParams.set("text", `⚽ Fútbol`)
      googleCalendarUrl.searchParams.set("dates", `${startDate}/${endDate}`)
      googleCalendarUrl.searchParams.set("location", match.mapsUrl)
      googleCalendarUrl.searchParams.set("details", description)

      window.open(googleCalendarUrl.toString(), "_blank")
      onShowNotification("¡Evento agregado a Google Calendar!", "success")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error adding to calendar:", error)
      onShowNotification("Error al agregar al calendario", "error")
    }
  }

  const handleExportList = async () => {
    try {
      if (onSyncTeams) {
        await onSyncTeams()
      }

      const response = await fetch(getApiUrl(`/api/matches/${match.id}/export`))
      const exportText = await response.text()

      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(exportText)
          onShowNotification("¡Lista copiada al portapapeles!", "success")
          return
        } catch (clipboardError) {
          // Fallback handled below
        }
      }

      const fallbackCopyTextToClipboard = (text: string) => {
        const textArea = document.createElement("textarea")
        textArea.value = text
        textArea.style.top = "0"
        textArea.style.left = "0"
        textArea.style.position = "fixed"
        textArea.style.opacity = "0"

        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        try {
          const successful = document.execCommand("copy")
          if (successful) {
            onShowNotification("¡Lista copiada al portapapeles!", "success")
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Fallback copy falló:", err)
          // Si el fallback también falla, mostrar modal para copia manual
          showTextForManualCopy(text)
        }

        document.body.removeChild(textArea)
      }

      // Función para mostrar texto para copia manual
      const showTextForManualCopy = (text: string) => {
        // Crear un modal temporal con el texto
        const modal = document.createElement('div');
        modal.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
        `;
        
        const content = document.createElement('div');
        content.style.cssText = `
          background: #23272A;
          border: 2px solid #00E676;
          border-radius: 12px;
          padding: 20px;
          max-width: 90%;
          max-height: 80%;
          overflow-y: auto;
          color: white;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        `;
        
        content.innerHTML = `
          <h3 style="margin: 0 0 15px 0; color: #00E676;">📋 Lista de Jugadores</h3>
          <p style="margin: 0 0 15px 0; color: #B0BEC5; font-size: 14px;">
            Copia manualmente el texto de abajo:
          </p>
          <textarea 
            readonly 
            style="
              width: 100%;
              min-height: 200px;
              background: #1a1a1a;
              border: 1px solid #333;
              border-radius: 8px;
              padding: 12px;
              color: white;
              font-family: monospace;
              font-size: 12px;
              resize: vertical;
              margin-bottom: 15px;
            "
          >${text}</textarea>
          <div style="text-align: center;">
            <button 
              onclick="this.parentElement.parentElement.parentElement.remove()"
              style="
                background: #00E676;
                color: #181A1B;
                border: none;
                border-radius: 8px;
                padding: 10px 20px;
                font-weight: bold;
                cursor: pointer;
                font-size: 14px;
              "
            >
              ✅ Cerrar
            </button>
          </div>
        `;
        
        modal.appendChild(content);
        document.body.appendChild(modal);
        
        // Auto-seleccionar el texto
        const textarea = content.querySelector('textarea') as HTMLTextAreaElement;
        if (textarea) {
          textarea.select();
          textarea.focus();
        }
        
        onShowNotification('Texto mostrado para copia manual', 'success');
      };

      fallbackCopyTextToClipboard(exportText)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error exporting players:", error)
      onShowNotification("Error al exportar la lista", "error")
    }
  }

  const handleShareLink = () => {
    setShowShareModal?.(true)
  }

  const ActionButton: React.FC<{
    onClick: () => void
    variant: "primary" | "secondary" | "muted" | "destructive"
    icon?: string
    children: React.ReactNode
    title?: string
    disabled?: boolean
  }> = ({ onClick, variant, icon, children, title, disabled = false }) => {
    const getVariantStyles = () => {
      switch (variant) {
        case "primary":
          return {
            backgroundColor: '#00E676',
            color: '#181A1B',
            border: '1px solid #00E676',
            boxShadow: '0 2px 8px rgba(0, 230, 118, 0.3)'
          }
        case "secondary":
          return {
            backgroundColor: '#2196F3',
            color: '#FFFFFF',
            border: '1px solid #2196F3',
            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)'
          }
        case "muted":
          return {
            backgroundColor: '#33383B',
            color: '#FFFFFF',
            border: '1px solid #33383B',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }
        case "destructive":
          return {
            backgroundColor: '#FF5722',
            color: '#FFFFFF',
            border: '1px solid #FF5722',
            boxShadow: '0 2px 8px rgba(255, 87, 34, 0.3)'
          }
        default:
          return {}
      }
    }

    return (
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '10px 16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          transition: 'all 0.2s ease',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          width: mobile ? '100%' : 'fit-content',
          minHeight: mobile ? '44px' : 'auto',
          minWidth: mobile ? '120px' : '160px',
          maxWidth: mobile ? '100%' : '250px',
          ...getVariantStyles()
        }}
        onClick={onClick}
        title={title}
        disabled={disabled}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.transform = 'translateY(-1px)'
            e.currentTarget.style.boxShadow = e.currentTarget.style.boxShadow.replace('0.3', '0.5')
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = e.currentTarget.style.boxShadow.replace('0.5', '0.3')
          }
        }}
      >
        {icon && <span style={{ fontSize: '16px' }}>{icon}</span>}
        {children}
      </button>
    )
  }

  const DropdownMenu: React.FC<{
    trigger: React.ReactNode
    children: React.ReactNode
    isOpen: boolean
    onToggle: () => void
  }> = ({ trigger, children, isOpen, onToggle }) => {
    return (
      <div style={{ position: 'relative' }}>
        <button type="button" onClick={onToggle} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>{trigger}</button>
        {isOpen && (
          <>
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 40
              }}
              onClick={onToggle}
              onKeyDown={(e) => { if (e.key === 'Escape') onToggle(); }}
              role="presentation"
            />
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              padding: '4px 0',
              minWidth: '220px',
              maxWidth: 'min(320px, calc(100vw - 32px))',
              zIndex: 50
            }}>
              {children}
            </div>
          </>
        )}
      </div>
    )
  }

  const MenuItem: React.FC<{
    onClick: () => void
    icon?: string
    children: React.ReactNode
    keepMenuOpen?: boolean
    testId?: string
  }> = ({ onClick, icon, children, keepMenuOpen = false, testId }) => (
    <button
      type="button"
      data-testid={testId}
      style={{
        padding: '8px 12px',
        fontSize: '14px',
        color: '#f1f5f9',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: 'transparent',
        border: 'none',
        width: '100%',
        textAlign: 'left'
      }}
      onClick={() => {
        onClick()
        if (!keepMenuOpen) {
          setShowAdminMenu(false)
          setShowUtilityMenu(false)
        }
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#334155'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent'
      }}
    >
      {icon && <span style={{ fontSize: '14px' }}>{icon}</span>}
      {children}
    </button>
  )

  // Mobile: Crear un menú desplegable principal
  if (mobile) {
    return (
      <div style={{
        width: '100%',
        padding: '0 16px'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#B0B0B0',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Acciones
          </h3>
          
          {/* Menú principal para mobile */}
          <DropdownMenu
            trigger={
              <ActionButton 
                onClick={() => setShowAdminMenu(!showAdminMenu)} 
                variant="primary" 
                icon="⚙️"
              >
                Acciones del Partido
                <span style={{
                  transition: 'transform 0.2s ease',
                  transform: showAdminMenu ? 'rotate(180deg)' : 'rotate(0deg)'
                }}>
                  ▼
                </span>
              </ActionButton>
            }
            isOpen={showAdminMenu}
            onToggle={() => setShowAdminMenu(!showAdminMenu)}
          >
            {/* Match Management Actions */}
            {userIsCreator && match.status === "SCHEDULED" && (
              <MenuItem onClick={onFinishMatch!} icon="🏁">
                Finalizar Partido
              </MenuItem>
            )}

            {/* Post-Match Actions */}
            {match.status === "FINISHED" && (
              <>
                {currentUser && onReportStats && (
                  <MenuItem onClick={onReportStats} icon="📊">
                    Reportar Mis Stats
                  </MenuItem>
                )}
                {onUploadFinalScore && userIsCreator && (
                  <MenuItem onClick={onUploadFinalScore} icon="🏆" testId="match-action-upload-final-score">
                    Subir Resultado Final
                  </MenuItem>
                )}
                {canVote && !reportCompleted && onVote && (
                  <MenuItem onClick={onVote} icon="⭐">
                    Votar Mejor Jugador
                  </MenuItem>
                )}
                {onRepeatMatch && userIsCreator && (
                  <MenuItem onClick={onRepeatMatch} icon="🔄">
                    Repetir Partido
                  </MenuItem>
                )}
              </>
            )}

            {/* Calendar Action */}
            {currentUser && match.status !== "FINISHED" && (
              <MenuItem onClick={handleAddToCalendar} icon="📅">
                Agregar a Calendario
              </MenuItem>
            )}

            {/* Admin Tools */}
            {userIsCreator && (
              <>
                {match.status === "FINISHED" && (
                  <>
                    {onLoadReport && (
                      <MenuItem onClick={onLoadReport} icon="📊">
                        {hasReport ? "✏️ Editar estadísticas" : "📊 Estadísticas del partido"}
                      </MenuItem>
                    )}
                    {onRecalculateMatchResult && match.team1Goals !== null && match.team2Goals !== null && (
                      <MenuItem onClick={onRecalculateMatchResult} icon="🔄">
                        Recalcular Resultado
                      </MenuItem>
                    )}
                  </>
                )}

                {match.status === "SCHEDULED" && (
                  <>
                    {onViewGuestRequests && (
                      <MenuItem onClick={onViewGuestRequests} icon="👥">
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          Solicitudes de invitados
                          {pendingGuestRequestsCount > 0 && (
                            <span style={{
                              backgroundColor: '#FF5722', color: '#FFF',
                              borderRadius: '10px', minWidth: '18px', height: '18px',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '11px', fontWeight: 700, padding: '0 4px',
                            }}>
                              {pendingGuestRequestsCount}
                            </span>
                          )}
                        </span>
                      </MenuItem>
                    )}
                    {onViewJoinApplications && (
                      <MenuItem onClick={onViewJoinApplications} icon="📨">
                        Postulaciones públicas
                      </MenuItem>
                    )}
                    {onManageGuests && (
                      <MenuItem onClick={onManageGuests} icon="➕">
                        Agregar invitado
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleExportList} icon="📋">
                      Exportar Lista
                    </MenuItem>
                    {onImportList && (
                      <MenuItem onClick={onImportList} icon="📥">
                        Importar Lista
                      </MenuItem>
                    )}
                    {onSuggestTeams && (
                      <MenuItem onClick={onSuggestTeams} icon="🎲">
                        Sugerir Equipos
                      </MenuItem>
                    )}
                  </>
                )}

                {onTransferAdmin && (
                  <MenuItem onClick={onTransferAdmin} icon="👑">
                    Transferir Admin
                  </MenuItem>
                )}

                {onCancelMatch && match.status === "SCHEDULED" && (
                  <MenuItem onClick={onCancelMatch} icon="❌">
                    Cancelar Partido
                  </MenuItem>
                )}
              </>
            )}

            {/* Proponer invitado - para jugadores no organizadores */}
            {!userIsCreator && isUserInMatch && match.status === "SCHEDULED" && onManageGuests && (
              <MenuItem onClick={onManageGuests} icon="👥">
                Proponer invitado
              </MenuItem>
            )}

            {/* Historial de Uniones - Solo para admin */}
            {userIsCreator && (
              <div style={{ borderTop: '1px solid #33383B' }}>
                <MatchJoinHistory
                  matchId={match.id}
                  matchName={`${match.team1Name || 'Equipo 1'} vs ${match.team2Name || 'Equipo 2'} - ${match.location}`}
                  userIsCreator={userIsCreator}
                />
              </div>
            )}

          </DropdownMenu>
        </div>
      </div>
    )
  }

  const iconBtnStyle: React.CSSProperties = {
    width: 28, height: 28, padding: 0,
    background: 'transparent', border: '1px solid #2A2F32',
    borderRadius: 6, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  // Desktop: Ultra-compact single row (36px)
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      height: 36,
      padding: '0 10px',
      borderTop: '1px solid #2A2F32',
    }}>
      {/* Primary action — small pill */}
      {userIsCreator && match.status === "SCHEDULED" && (
        <button onClick={onFinishMatch!} style={{ height: 26, padding: '0 10px', borderRadius: 4, border: 'none', background: '#FF5722', color: '#FFF', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
          FINALIZAR
        </button>
      )}

      {/* Suggest teams — primary action for organizers pre-match */}
      {userIsCreator && match.status === "SCHEDULED" && onSuggestTeams && (
        <button
          onClick={onSuggestTeams}
          style={{ height: 26, padding: '0 10px', borderRadius: 4, border: 'none', background: '#00E676', color: '#181A1B', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          title="Sugerir equipos balanceados"
        >
          <span>🎲</span>
          SUGERIR EQUIPOS
        </button>
      )}

      {match.status === "FINISHED" && (
        <>
          {currentUser && onReportStats && (
            <button onClick={onReportStats} style={{ height: 26, padding: '0 10px', borderRadius: 4, border: 'none', background: '#00E676', color: '#181A1B', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              MIS STATS
            </button>
          )}
          {onRepeatMatch && userIsCreator && (
            <button onClick={onRepeatMatch} style={{ height: 26, padding: '0 10px', borderRadius: 4, border: 'none', background: '#2196F3', color: '#FFF', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              REPETIR
            </button>
          )}
        </>
      )}

      {/* Share — icon only */}
      {match.status !== "FINISHED" && (
        <button onClick={handleShareLink} style={iconBtnStyle} title="Compartir partido">
          <Share2 size={15} color="#888" />
        </button>
      )}

      {/* View toggle: Cancha / Lista */}
      {canViewTactics && onSetTacticalField && (
        <div style={{
          display: 'inline-flex',
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 13,
          padding: 2,
          border: '1px solid #2A2F32',
          height: 26,
          alignItems: 'center',
        }}>
          {[
            { active: !!showTacticalField, label: 'Cancha', value: true },
            { active: !showTacticalField, label: 'Lista', value: false },
          ].map(opt => (
            <button
              key={opt.label}
              onClick={() => onSetTacticalField(opt.value)}
              style={{
                display: 'flex', alignItems: 'center',
                padding: '0 8px', height: 22, borderRadius: 11, border: 'none',
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: opt.active ? '#00E676' : 'transparent',
                color: opt.active ? '#181A1B' : '#555',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Overflow menu — icon only */}
      <DropdownMenu
        trigger={
          <button type="button" data-testid="match-actions-more" onClick={() => setShowAdminMenu(!showAdminMenu)} style={iconBtnStyle} title="Mas acciones">
            <MoreHorizontal size={16} color="#888" />
          </button>
        }
        isOpen={showAdminMenu}
        onToggle={() => setShowAdminMenu(!showAdminMenu)}
      >
        {/* Calendar */}
        {currentUser && match.status !== "FINISHED" && (
          <MenuItem onClick={handleAddToCalendar} icon="📅">
            Agregar a Calendario
          </MenuItem>
        )}

        {/* Export list - available for everyone */}
        {match.status !== "FINISHED" && (
          <MenuItem onClick={handleExportList} icon="📋">
            Exportar Lista
          </MenuItem>
        )}

        {/* Post-match admin actions */}
        {match.status === "FINISHED" && userIsCreator && (
          <>
            {onUploadFinalScore && (
              <MenuItem onClick={onUploadFinalScore} icon="🏆" testId="match-action-upload-final-score">
                Subir Resultado Final
              </MenuItem>
            )}
            {onLoadReport && (
              <MenuItem onClick={onLoadReport} icon="📊">
                {hasReport ? "✏️ Editar estadísticas" : "📊 Estadísticas del partido"}
              </MenuItem>
            )}
            {onRecalculateMatchResult && match.team1Goals !== null && match.team2Goals !== null && (
              <MenuItem onClick={onRecalculateMatchResult} icon="🔄">
                Recalcular Resultado
              </MenuItem>
            )}
          </>
        )}

        {match.status === "FINISHED" && canVote && !reportCompleted && onVote && (
          <MenuItem onClick={onVote} icon="⭐">
            Votar Mejor Jugador
          </MenuItem>
        )}

        {/* Scheduled admin actions */}
        {userIsCreator && match.status === "SCHEDULED" && (
          <>
            {onViewGuestRequests && (
              <MenuItem onClick={onViewGuestRequests} icon="👥">
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Solicitudes de invitados
                  {pendingGuestRequestsCount > 0 && (
                    <span style={{
                      backgroundColor: '#FF5722', color: '#FFF',
                      borderRadius: '10px', minWidth: '18px', height: '18px',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: 700, padding: '0 4px',
                    }}>
                      {pendingGuestRequestsCount}
                    </span>
                  )}
                </span>
              </MenuItem>
            )}
            {onManageGuests && (
              <MenuItem onClick={onManageGuests} icon="➕">
                Agregar invitado
              </MenuItem>
            )}
            {onViewJoinApplications && (
              <MenuItem onClick={onViewJoinApplications} icon="📨">
                Postulaciones públicas
              </MenuItem>
            )}
            {onImportList && (
              <MenuItem onClick={onImportList} icon="📥">
                Importar Lista
              </MenuItem>
            )}
          </>
        )}

        {/* Non-organizer: propose guest */}
        {!userIsCreator && isUserInMatch && match.status === "SCHEDULED" && onManageGuests && (
          <MenuItem onClick={onManageGuests} icon="👥">
            Proponer invitado
          </MenuItem>
        )}

        {/* Admin-only tools */}
        {userIsCreator && (
          <>
            {onTransferAdmin && (
              <MenuItem onClick={onTransferAdmin} icon="👑">
                Transferir Admin
              </MenuItem>
            )}
            {onCancelMatch && match.status === "SCHEDULED" && (
              <MenuItem onClick={onCancelMatch} icon="❌">
                Cancelar Partido
              </MenuItem>
            )}
            {/* Join history */}
            <div style={{ borderTop: '1px solid #33383B' }}>
              <MatchJoinHistory
                matchId={match.id}
                matchName={`${match.team1Name || 'Equipo 1'} vs ${match.team2Name || 'Equipo 2'} - ${match.location}`}
                userIsCreator={userIsCreator}
              />
            </div>
          </>
        )}
      </DropdownMenu>

      {/* Edit pencil — creator only */}
      {userIsCreator && onEditLocation && (
        <button onClick={onEditLocation} style={iconBtnStyle} title="Editar datos del partido">
          <Edit3 size={14} color="#888" />
        </button>
      )}
    </div>
  )
}
